/**
 * The Invasion Director.
 *
 * Three jobs:
 *   1. Decide how much trouble the next wave is worth (the threat budget).
 *   2. Spend that budget on a composition — partly from the faction's normal
 *      doctrine, partly as a deliberate counter to how the player has been
 *      defending.
 *   3. Walk it on stage through the map-edge gates, staggered, from the
 *      directions the scouting panel already told the player about.
 *
 * The fairness rule is load-bearing: everything the director decides is visible
 * during the build phase *before* it happens. Difficulty comes from the player's
 * choices, never from surprise.
 */

import { BUILD_PHASE_SECONDS, MIN_BUILD_PHASE_SECONDS } from '../constants.ts';
import { Phase, RESOURCE_KEYS, RESOURCE_LABELS, Team, UF, UnitState } from '../types.ts';
import { EV, LogTone, pushEvent, pushLog } from '../events.ts';
import { statsOf, type SpawnOrder, type World } from '../world.ts';
import { createCompany, fillCompany } from './companies.ts';
import { awardHeroXp, HERO_XP_PER_WAVE, HERO_XP_PER_WAVE_STEP } from './hero.ts';
import { rollHoard } from './items.ts';
import { unitType, UNIT_TYPES, unitTypeIndex } from '../../data/units.ts';
import { faction, INVADER_FACTIONS, PLAYABLE_FACTIONS } from '../../data/factions.ts';
import { buildingType } from '../../data/buildings.ts';
import { chance, nextFloat, nextInt, pick, pickWeighted, shuffle } from '../../core/rng.ts';
import { chronicleEvent, eligibleEvents, offeredChoices, type ChronicleEventDef } from '../../data/events.ts';
import { clamp, clamp01 } from '../../core/math.ts';
import { AGE_BUILDING_REQUIREMENTS, AGE_RESEARCH_THREAT, ageCost, ageHearthTier, ageResearchSeconds, ageWaveFloor, MAX_AGE } from '../../data/ages.ts';
import { crownAfford, crownPool } from '../campaign/commonwealth.ts';
import { BUILDING_TYPES } from '../../data/buildings.ts';
import { gateDirName } from '../map.ts';
import { advanceGuidedRun, shapeGuidedWave } from './tutorial.ts';
import { WallKind } from '../types.ts';

/** Which champion heads up the Siege on this wave. */
export function championForWave(wave: number): (typeof SIEGE_CHAMPIONS)[number] | undefined {
  if (wave <= 0 || wave % 10 !== 0) return undefined;
  const n = Math.floor(wave / 10) - 1;
  return SIEGE_CHAMPIONS[n % SIEGE_CHAMPIONS.length];
}

/** Named champions that head up every tenth wave. */
export const SIEGE_CHAMPIONS = [
  {
    unit: 'iron_warlord',
    name: 'Grelt, Hollow of the Ninth Bell',
    title: 'GATE-BREAKER',
    mechanic: 'ramfocus',
    blurb: 'Brings a ram convoy. Kill the ram before it reaches the gate, or fight him inside your own walls.',
  },
  {
    unit: 'gutter_bloatpriest',
    name: 'The Ninth Homily',
    title: 'UNDER-SERMON',
    mechanic: 'undermine',
    blurb: 'Its congregation surfaces behind your wall line. Walls will not help you; bodies will.',
  },
  {
    unit: 'rot_sporeherald',
    name: 'Mother Cauldwell',
    title: 'THE SECOND HARVEST',
    mechanic: 'reanimate',
    blurb: 'Reanimates your fallen. Every soldier you lose joins the other side.',
  },
  {
    unit: 'drowned_abyssal',
    name: 'That Which Came Up The Weeping Ford',
    title: 'THE THIRD PART',
    mechanic: 'madness',
    blurb: 'Fog. Your companies will forget whose banner they follow.',
  },
  {
    unit: 'litany_charnelox',
    name: 'The Sixth Ox of the Pale Court',
    title: 'PROCESSIONAL',
    mechanic: 'trample',
    blurb: 'Ignores your line entirely and walks at the Hearth. Nothing you own will stop it alone.',
  },
];

/** Wave modifiers, all telegraphed on the scouting panel. */
const MODIFIERS = [
  { id: 'nightfall', name: 'Nightfall', text: 'Cold dark. Your archers see two tiles less.' },
  { id: 'ashstorm', name: 'Ash Storm', text: 'Ash on the wind. Everything moves slower, both ways.' },
  { id: 'bloodmoon', name: 'Blood Moon', text: 'The horde does not rout.' },
  { id: 'famine', name: 'Famine Wind', text: 'No grain is produced while the wave stands.' },
  { id: 'swiftness', name: 'The Long Run', text: 'They come faster than they should.' },
  { id: 'rain', name: 'Black Rain', text: 'Fires gutter. Fire damage is halved.' },
];

/**
 * Is this wave modifier in force right now?
 *
 * `incomingModifiers` is planned during the build phase and describes the wave
 * that is *coming*; once the horn sounds it describes the wave that is here.
 * So a modifier is in force exactly while a Siege is running — which is also
 * what makes it fair, because the scouting panel showed the player the same
 * list before they chose to sound the horn.
 *
 * Four of these six were declared with player-facing text and read by nothing:
 * the panel promised "no grain is produced while the wave stands" and grain
 * production carried on regardless. That is the director's own fairness rule
 * broken backwards — not a hidden difficulty, but a promised one that never
 * arrived.
 */
export function waveModifier(w: World, id: string): boolean {
  return w.phase === Phase.Siege && w.incomingModifiers.includes(id);
}

// ── Budget ─────────────────────────────────────────────────────────────────

export function waveBudget(w: World, wave: number): number {
  const d = w.difficulty;
  // S-curve with a step at each Age boundary.
  const base = 44 * Math.pow(wave, 1.18 * d.curveMul);
  // The opening waves are deliberately gentle: the first quarter of an hour is
  // for laying out a town, not for surviving one.
  const easing = wave <= 6 ? 0.7 + wave * 0.05 : 1;
  const ageStep = 1 + 0.12 * (w.age - 1);
  const siege = wave % 10 === 0 ? 1.45 : 1;
  // A settlement with its best people bent over plans is a settlement paying
  // less attention to its walls, and the horde can tell. This is the price of
  // climbing, and the scouting report names it before the player commits.
  const climbing = w.researchingAge > 0 ? AGE_RESEARCH_THREAT : 1;
  return base * easing * d.budgetMul * ageStep * siege * climbing;
}

/**
 * Which tiers of unit the director may field this deep into the run.
 *
 * The ladder runs about a dozen waves behind the player's own wave floors —
 * they may commission the Bell at 15 and meet its enemies at 24 — and that lag
 * is the point. An Age is supposed to be a window in which you are briefly the
 * better-armed side, and the horde catching up is what closes it.
 *
 * The table stopped at 4, which was correct while the ladder did. It is not any
 * more: the fifth Age's units are on every faction's roster and the director
 * could never draw one, so a settlement that had paid for the Hollow would climb
 * into it and find the same wave it had been fighting since wave 40. The last
 * rung lands at 56, fifteen past the Hollow's own floor, which is the widest lag
 * of the four — the top of the ladder is where the player has earned the longest
 * look at what they bought.
 */
export function maxEnemyTier(wave: number): number {
  if (wave >= 56) return 5;
  if (wave >= 40) return 4;
  if (wave >= 24) return 3;
  if (wave >= 10) return 2;
  return 1;
}

// ── Planning ───────────────────────────────────────────────────────────────

export function planNextWave(w: World): void {
  const wave = w.wave + 1;
  const rng = w.rngDirector;
  const budget = waveBudget(w, wave);
  w.incomingBudget = budget;

  // Faction: rotates, with playable factions appearing as inverted invaders later.
  const pool = wave >= 15 ? [...INVADER_FACTIONS, ...PLAYABLE_FACTIONS.filter((f) => f.id !== w.faction.id)] : INVADER_FACTIONS;
  const weights = pool.map((f) => (f.playable ? 0.4 : 1));
  const fac = pickWeighted(rng, pool, weights);
  w.incomingFaction = fac.id;

  // Gates: more of them, later.
  const gateCount = wave >= 45 ? 4 : wave >= 28 ? 3 : wave >= 12 ? 2 : 1;
  const order = shuffle(rng, w.map.gates.map((_, i) => i));
  w.incomingGates = order.slice(0, gateCount);

  // Modifiers.
  w.incomingModifiers = [];
  if (wave >= 6 && chance(rng, Math.min(0.6, 0.12 + wave * 0.012))) {
    w.incomingModifiers.push(pick(rng, MODIFIERS).id);
  }
  /**
   * There is no `'siege'` modifier, and there never was.
   *
   * Every tenth wave used to push that id into `incomingModifiers`, where
   * `MODIFIERS` had no entry for it, `scoutingReport` filtered it straight back
   * out of the panel, and `waveModifier` was never asked about it by anything.
   * A write-only sentinel: it changed the digest and nothing else. What a tenth
   * wave actually *is* lives in two real places — the 1.45x in `waveBudget`
   * above, and the named champion from `championForWave`, which the scouting
   * panel does show. Both were already there. This line only ever made a
   * promise the game had no way to keep.
   */

  // Composition.
  const roster = fac.roster
    .map((id) => unitTypeIndex(id))
    .filter((idx) => unitType(idx).tier <= maxEnemyTier(wave));
  const usable = roster.length > 0 ? roster : fac.roster.map((id) => unitTypeIndex(id)).slice(0, 1);

  const comp = new Map<number, number>();
  let remaining = budget;

  // 1. Doctrine spend — the faction's own idea of a war party.
  const adaptShare = clamp01(w.difficulty.adaptCap) * (w.profile.samples > 0 ? 1 : 0);
  const doctrineBudget = remaining * (1 - adaptShare);
  remaining -= doctrineBudget;
  spendOnRoles(w, comp, usable, doctrineBudget, wave);

  // 2. Counter spend — capped, so it pressures without feeling like cheating.
  if (remaining > 0) spendOnCounters(w, comp, usable, remaining, wave);

  // 3. Champion, every tenth wave.
  if (wave % 10 === 0) {
    const champ = championForWave(wave);
    if (champ) {
      const idx = unitTypeIndex(champ.unit);
      comp.set(idx, (comp.get(idx) ?? 0) + 1);
    }
  }

  w.incomingComposition = [...comp.entries()]
    .map(([typeIdx, count]) => ({ typeIdx, count }))
    .sort((a, b) => unitType(b.typeIdx).threat * b.count - unitType(a.typeIdx).threat * a.count);

  // The guided run, last and only while it is running: a lesson's wave is an
  // ordinary wave with its budget, gate, faction and composition bent to teach
  // one verb. A no-op on every other run. See `systems/tutorial.ts`.
  shapeGuidedWave(w);
}

/** Backbone / shot / specialist / heavy, in roughly that proportion. */
function spendOnRoles(
  w: World,
  comp: Map<number, number>,
  roster: number[],
  budget: number,
  wave: number,
): void {
  const rng = w.rngDirector;
  const backbone = roster.filter((i) => isBackbone(i));
  const shot = roster.filter((i) => (unitType(i).flags & UF.Ranged) !== 0);
  const heavy = roster.filter((i) => (unitType(i).flags & (UF.Large | UF.Siege)) !== 0);
  const rest = roster.filter((i) => !isBackbone(i) && (unitType(i).flags & UF.Ranged) === 0);

  const shares: Array<[number[], number]> = [
    [backbone.length ? backbone : roster, 0.45],
    [shot.length ? shot : roster, wave < 3 ? 0.15 : 0.25],
    [rest.length ? rest : roster, 0.2],
    [heavy.length && wave >= 6 ? heavy : backbone.length ? backbone : roster, wave < 6 ? 0 : 0.1],
  ];

  for (const [pool, share] of shares) {
    let spend = budget * share;
    let guard = 0;
    while (spend > 0 && guard++ < 60) {
      const idx = pick(rng, pool);
      const t = unitType(idx);
      const packSize = Math.max(1, Math.round(t.companySize * (0.55 + nextFloat(rng) * 0.6)));
      const cost = t.threat * packSize;
      if (cost > spend * 1.6 && packSize > 1) {
        // Too expensive for what is left: buy as many as the purse allows and
        // move on to the next role.
        const fit = Math.max(1, Math.floor(spend / Math.max(1, t.threat)));
        comp.set(idx, (comp.get(idx) ?? 0) + fit);
        break;
      }
      comp.set(idx, (comp.get(idx) ?? 0) + packSize);
      spend -= cost;
    }
  }
}

function isBackbone(i: number): boolean {
  const t = unitType(i);
  return t.tier <= 2 && (t.flags & (UF.Ranged | UF.Large | UF.Siege | UF.Caster | UF.Champion)) === 0;
}

/**
 * Read the player's last few waves and buy the answer.
 * Each branch is a specific, legible response the player can pre-empt.
 */
function spendOnCounters(
  w: World,
  comp: Map<number, number>,
  roster: number[],
  budget: number,
  wave: number,
): void {
  const p = w.profile;
  const picks: Array<{ want: (i: number) => boolean; weight: number }> = [
    // Archer stacks → shields and pavises.
    { want: (i) => (unitType(i).flags & UF.Shielded) !== 0, weight: p.archerRatio * 2.2 },
    // Turtled walls → sappers and siege.
    { want: (i) => (unitType(i).flags & (UF.Sapper | UF.Siege)) !== 0, weight: p.wallIntegrity * 2.4 },
    // Choke-point abuse → fliers and burrowers that ignore the choke.
    {
      want: (i) => (unitType(i).flags & UF.Flying) !== 0 || unitType(i).ability === 'burrow',
      weight: p.chokeReliance * 2.0,
    },
    // Heavy melee mass → ranged and poison area denial.
    { want: (i) => (unitType(i).flags & UF.Ranged) !== 0, weight: p.meleeMass * 1.5 },
    // Exposed economy → fast raiders.
    { want: (i) => unitType(i).speed >= 2.0, weight: p.economyExposure * 1.8 },
    // Heavy casting → spell-eaters.
    { want: (i) => (unitType(i).flags & UF.SpellEater) !== 0, weight: p.spellUse * 2.6 },
  ];

  const rng = w.rngDirector;
  let spend = budget;
  let guard = 0;
  while (spend > 0 && guard++ < 40) {
    const totalW = picks.reduce((s, x) => s + Math.max(0, x.weight), 0);
    if (totalW <= 0) break;
    const choice = pickWeighted(
      rng,
      picks,
      picks.map((x) => Math.max(0, x.weight)),
    );
    const pool = roster.filter(choice.want);
    if (pool.length === 0) {
      choice.weight = 0;
      continue;
    }
    const idx = pick(rng, pool);
    const t = unitType(idx);
    const packSize = Math.max(1, Math.round(t.companySize * 0.7));
    const cost = t.threat * packSize;
    /**
     * Buy what the purse will bear, exactly as the doctrine spend above does.
     *
     * This used to take the whole pack whatever it cost, which was worth about
     * a hundred points of overshoot while a pack was eight men. At the
     * establishments this pass raised, a pack is nearer four hundred, and a
     * counter arm that walked off the end of its share was handing the wave a
     * tenth again of the strength the scouting panel had promised — the
     * director's own fairness rule, broken by arithmetic rather than by design.
     */
    if (cost > spend * 1.4 && packSize > 1) {
      const fit = Math.max(1, Math.floor(spend / Math.max(1, t.threat)));
      comp.set(idx, (comp.get(idx) ?? 0) + fit);
      break;
    }
    comp.set(idx, (comp.get(idx) ?? 0) + packSize);
    spend -= cost;
    if (wave < 8) break; // early waves get at most one counter pack
  }
}

// ── Scouting text ──────────────────────────────────────────────────────────

export function scoutingReport(w: World): {
  faction: string;
  gates: string[];
  strength: string;
  composition: Array<{ name: string; count: number; note: string }>;
  modifiers: Array<{ name: string; text: string }>;
  champion?: { name: string; title: string; blurb: string };
} {
  const fac = faction(w.incomingFaction);
  const gates = w.incomingGates.map((gi) => {
    const g = w.map.gates[gi];
    return g ? `${g.name} (${gateDirName(g.dir)})` : 'unknown';
  });
  const total = w.incomingComposition.reduce(
    (s, c) => s + unitType(c.typeIdx).threat * c.count,
    0,
  );
  const strength =
    total < 200 ? 'a raiding party' : total < 700 ? 'a warband' : total < 1800 ? 'a host' : 'an army';

  const composition = w.incomingComposition.slice(0, 8).map((c) => {
    const t = unitType(c.typeIdx);
    return { name: t.name, count: c.count, note: noteFor(t.flags) };
  });

  const modifiers = w.incomingModifiers
    .map((id) => MODIFIERS.find((m) => m.id === id))
    .filter((m): m is (typeof MODIFIERS)[number] => !!m)
    .map((m) => ({ name: m.name, text: m.text }));

  const wave = w.wave + 1;
  const champion = championForWave(wave);

  return {
    faction: fac.name,
    gates,
    strength,
    composition,
    modifiers,
    champion: champion
      ? { name: champion.name, title: champion.title, blurb: champion.blurb }
      : undefined,
  };
}

function noteFor(flags: number): string {
  /**
   * Flying is tested first, and above `Siege`, because it is the note that
   * changes the most about what the player should do next.
   *
   * The scouting panel is the director's fairness rule made visible — it says
   * what is coming so the answer can be bought before it arrives. A flier is
   * the one arrival against which the *standing* answer, the curtain, is worth
   * nothing at all, and it is therefore the one that most needs saying out
   * loud. Before this line the panel called a wyvern "heavy — needs a real
   * answer" and a gryphon "fast — spears or nothing", which is worse than
   * silence: both send the player to buy a wall and a spear line.
   */
  if ((flags & UF.Flying) !== 0) return 'wings — it comes over the wall, not through it';
  if ((flags & UF.Siege) !== 0) return 'siege — kill it before the gate';
  if ((flags & UF.Sapper) !== 0) return 'sappers — they want your walls';
  if ((flags & UF.Shielded) !== 0) return 'shields — arrows will skip off';
  if ((flags & UF.Cavalry) !== 0) return 'fast — spears or nothing';
  if ((flags & UF.Ranged) !== 0) return 'shot — do not stand still';
  if ((flags & UF.Large) !== 0) return 'heavy — needs a real answer';
  if ((flags & UF.Caster) !== 0) return 'casters — reach them early';
  return '';
}

// ── Wave lifecycle ─────────────────────────────────────────────────────────

/**
 * How fast a gate lets the horde through, in models a second.
 *
 * Ten, which is what the old per-group stagger worked out to at the old
 * establishments — twenty groups of about eleven men over about thirty-four
 * seconds. Written down as a rate so that changing how big a block is cannot
 * silently change how long a wave takes to arrive.
 */
const GATE_MODELS_PER_SEC = 10;

export function beginSiege(w: World): void {
  w.wave++;
  w.phase = Phase.Siege;
  // Both of these are "once per Siege" promises, and this is the one place a
  // Siege begins.
  w.hearthSaved = false;
  for (const c of w.companies) c.usedSecondWind = false;
  // During a siege `phaseT` counts *up*: it is how long this wave has been
  // going, which the patience rule above reads.
  w.phaseT = 0;
  w.spawnQueue = [];

  const rng = w.rngDirector;
  const gates = w.incomingGates.length > 0 ? w.incomingGates : [0];
  let gi = 0;
  let delay = 0.5;

  for (const entry of w.incomingComposition) {
    const t = unitType(entry.typeIdx);
    let left = entry.count;
    while (left > 0) {
      const take = Math.min(left, Math.max(1, t.companySize));
      const boss = (t.flags & UF.Champion) !== 0;
      // Fast units with no siege value go economy-hunting.
      const raider = t.speed >= 2.2 && (t.flags & (UF.Siege | UF.Sapper)) === 0 && chance(rng, 0.35);
      w.spawnQueue.push({
        typeIdx: entry.typeIdx,
        count: take,
        gate: gates[gi % gates.length],
        t: boss ? 3 : delay,
        boss,
        flowKind: raider ? 1 : 0,
      });
      gi++;
      /**
       * The wave walks on at a rate of *men*, not of blocks.
       *
       * This used to be a flat 0.9–2.5 s between groups, which was a cadence in
       * blocks — fine while a block was a dozen men, and wrong the moment the
       * establishments tripled: the same three hundred invaders arrived in eight
       * groups instead of twenty-five and the whole horde was on the field in
       * twelve seconds instead of thirty-five. That is a different siege. A
       * gate passes so many men a second, so the wait is charged against the
       * size of the block coming through it, and a wave of a given strength
       * takes the same time to arrive as it always did (measured: 33 s before,
       * 34 s after, at wave 17).
       */
      delay += (take / GATE_MODELS_PER_SEC) * (0.75 + nextFloat(rng) * 0.5);
      left -= take;
    }
  }

  pushEvent(w.events, EV.WaveStart, w.map.hearthX, w.map.hearthY, w.wave, 0, Team.Invader);
  const fac = faction(w.incomingFaction);
  pushLog(w.events, `Wave ${w.wave}. ${fac.name} come.`, LogTone.Dire);
  const champ = championForWave(w.wave);
  if (champ) {
    pushEvent(
      w.events,
      EV.Boss,
      w.map.hearthX,
      w.map.hearthY,
      0,
      0,
      Team.Invader,
      0,
      0,
      `${champ.name}|${champ.title}`,
    );
  }
}

/**
 * After this long in a single siege, the horde stops raiding the outskirts and
 * goes for the Hearth.
 *
 * Without it a losing player does not lose: the invaders spend twenty minutes
 * eating outlying farms while the Hearth sits untouched, the wave never clears
 * because nothing has died, and the run neither ends nor progresses. A siege has
 * to resolve one way or the other.
 */
const SIEGE_PATIENCE = 150;
/** And after this long, the ones that are left start going home. */
/**
 * Seconds of siege after which the horde starts giving up. Exported for the
 * rout logic in `combat.ts`: past this point a router whose clock runs out
 * *escapes in place* rather than rallying — see the note there for the trap
 * that made "runs for the edge" an empty promise.
 */
export const SIEGE_BREAKING_POINT = 240;

export function stepDirector(w: World, dt: number): void {
  if (w.phase !== Phase.Siege) return;

  w.phaseT += dt;
  if (w.phaseT > SIEGE_PATIENCE) {
    // Stop raiding. Everything that is left goes for the Hearth.
    for (const c of w.companies) {
      if (c.team !== Team.Invader || c.dead) continue;
      c.flowKind = 0;
    }
    if (w.tick % 30 === 0) {
      const u = w.units;
      for (let i = 0; i < u.slots.high; i++) {
        if (u.slots.alive[i] === 0 || u.team[i] !== Team.Invader) continue;
        u.targetBuilding[i] = -1;
      }
    }
  }

  // And past the breaking point, they withdraw — a growing share of the horde
  // gives it up each second and runs for the edge, where routers leave the
  // field. This is what guarantees a siege terminates: without it a stalemate
  // between a small garrison and a large horde simply runs until the heat death
  // of the run, and neither side ever gets a decision.
  if (w.phaseT > SIEGE_BREAKING_POINT && w.tick % 30 === 0) {
    const over = w.phaseT - SIEGE_BREAKING_POINT;
    const share = Math.min(0.35, 0.02 + over / 900);
    const u = w.units;
    let quit = 0;
    for (let i = 0; i < u.slots.high; i++) {
      if (u.slots.alive[i] === 0 || u.team[i] !== Team.Invader) continue;
      if (u.state[i] === UnitState.Routing) continue;
      if (chance(w.rngDirector, share)) {
        u.state[i] = UnitState.Routing;
        u.stateT[i] = 90;
        u.morale[i] = 0;
        u.fearT[i] = 60;
        quit++;
      }
    }
    if (quit > 0 && w.phaseT < SIEGE_BREAKING_POINT + 2) {
      pushLog(w.events, 'They have had enough of you. Some of them, anyway.', LogTone.Good);
    }
  }

  // Walk the queued arrivals on stage.
  let arrived = 0;
  for (let i = w.spawnQueue.length - 1; i >= 0; i--) {
    const s = w.spawnQueue[i];
    s.t -= dt;
    if (s.t > 0) continue;
    arrived += spawnGroup(w, s);
    w.spawnQueue.splice(i, 1);
  }

  /**
   * Wave cleared?
   *
   * `w.counts` is rebuilt by `recount` at the *end* of the tick, so the value
   * read here is always from the end of the previous one. That is the right
   * answer for everything the director does — except for the men it has just
   * this instant walked onto the field. When the last queued group landed on a
   * tick where the field happened to be clear, the stale zero fired `endSiege`
   * with a whole company standing in the settlement: the wave scored as
   * survived, villagers went back to work, and the horde stayed. Roughly one
   * wave-end in three in a bot sweep.
   */
  if (w.spawnQueue.length === 0 && w.counts.enemy === 0 && arrived === 0) {
    endSiege(w);
  }
}

/** Returns how many models actually made it onto the field. */
function spawnGroup(w: World, s: SpawnOrder): number {
  const g = w.map.gates[s.gate] ?? w.map.gates[0];
  if (!g) return 0;
  const t = unitType(s.typeIdx);
  const c = createCompany(w, s.typeIdx, Team.Invader, g.x + 0.5, g.y + 0.5);
  c.flowKind = s.flowKind;
  c.gate = s.gate;
  c.boss = s.boss;
  c.maxSize = Math.max(c.maxSize, s.count);
  const made = fillCompany(w, c, s.count, g.x + 0.5, g.y + 0.5);

  // Blood Moon: this horde does not break.
  if (w.incomingModifiers.includes('bloodmoon')) {
    for (const h of c.members) {
      const i = w.units.resolve(h);
      if (i >= 0) w.units.flags[i] |= UF.Undead;
    }
  }
  if (w.incomingModifiers.includes('swiftness')) {
    for (const h of c.members) {
      const i = w.units.resolve(h);
      if (i >= 0) w.units.speed[i] *= 1.25;
    }
  }

  pushEvent(w.events, EV.Spawn, g.x + 0.5, g.y + 0.5, s.count, s.typeIdx, Team.Invader);
  if (s.boss) {
    pushLog(w.events, `${t.name} sets foot on the field.`, LogTone.Dire);
  }
  return made;
}

function endSiege(w: World): void {
  const stats = statsOf(w);
  stats.wavesSurvived = w.wave;
  sampleProfile(w);
  // Surviving the night is the largest single thing a hero learns from.
  awardHeroXp(w, HERO_XP_PER_WAVE + w.wave * HERO_XP_PER_WAVE_STEP);

  w.phase = Phase.Build;
  // Build phases stay long. They shorten slowly as the run goes on, and never
  // below a floor that still leaves room to lay out a district properly.
  w.phaseT =
    Math.max(MIN_BUILD_PHASE_SECONDS, BUILD_PHASE_SECONDS - w.wave * 1.4) * w.difficulty.buildTimeMul;
  w.will = Math.min(w.willMax, w.will + w.mods.willOnWaveEnd);
  w.morale += 0.08;

  pushEvent(w.events, EV.WaveEnd, w.map.hearthX, w.map.hearthY, w.wave, 0, Team.Player);
  pushLog(w.events, `Wave ${w.wave} is done. Count the standing.`, LogTone.Good);

  // And then somebody walks the field. Half of them turn something up; it rolls
  // off its own stream, so what the dead were carrying cannot shift what the
  // next wave is made of.
  rollHoard(w, 0);

  // Age progression is no longer here. An Age is bought and waited for — see
  // `stepAgeResearch` and `src/data/ages.ts`. Surviving waves is what earns you
  // the *right* to begin one, via the wave floor; it no longer hands you one.

  // The lesson the player is about to be taught, before the wave that examines
  // it is planned. May also lengthen the build phase just set above — the
  // muster is the verb people fail, and it is the one given room.
  advanceGuidedRun(w);
  planNextWave(w);
  // Last, so a Chronicle choice that re-weights the coming wave is writing over
  // a plan that already exists rather than one that is about to be replaced.
  maybeDrawChronicle(w);
}

/**
 * Five, and the Hollow Age is the last of them.
 *
 * 'Endless' was a sixth, gated behind wave 60 — past the end of most runs, so
 * it was a name in an array rather than an Age anybody played. Cutting it makes
 * Hollow reachable at wave 41 instead of being the penultimate step to a place
 * nobody went.
 */
export const AGE_NAMES = ['Ember', 'Iron', 'Bell', 'Gilded', 'Hollow'];

// ── The Chronicle ──────────────────────────────────────────────────────────

/**
 * Events are the director's other job.
 *
 * They are drawn here rather than from a scheduler of their own, for the same
 * reason waves are: there is exactly one place in this codebase that decides
 * what happens to the player between one siege and the next, and two of them
 * would drift apart inside a week.
 *
 * The draw happens at the end of a Siege, never during one. That is a design
 * decision and not an accident of where the call sits: a pending event freezes
 * the world, and freezing the world with a horde halfway across the field would
 * be a worse experience than any event is worth.
 */
const CHRONICLE_FIRST_WAVE = 2;
/**
 * Waves that must pass between one event and the next.
 *
 * One, not two. The deck is large enough now that a gap of two left most of it
 * unseen in a run of ordinary length — a card that only fires when you are broke
 * and losing is worth nothing if the settlement is only asked a question every
 * fifth wave. The odds below still average the gap out to nearer three.
 */
const CHRONICLE_GAP = 1;

export function maybeDrawChronicle(w: World): void {
  if (w.pendingEvent || w.battle) return;
  if (w.wave < CHRONICLE_FIRST_WAVE) return;
  const since = w.wave - w.lastEventWave;
  if (since < CHRONICLE_GAP) return;

  // The roll is taken on every eligible wave-end whether or not the deck has
  // anything to offer, so the cursor advances on a schedule that depends only
  // on the wave number and never on which events happen to be eligible.
  const odds = Math.min(0.85, 0.42 + 0.12 * (since - CHRONICLE_GAP));
  const drew = chance(w.rngEvent, odds);
  const pool = eligibleEvents(w, w.eventHistory);
  if (!drew || pool.length === 0) return;

  const def = pickWeighted(w.rngEvent, pool, pool.map((e) => Math.max(0.01, e.weight)));
  openChronicle(w, def);
}

/** Put an event in front of the player. Also the debug console's entry point. */
export function openChronicle(w: World, def: ChronicleEventDef): boolean {
  if (w.pendingEvent) return false;
  const choices = offeredChoices(w, def);
  if (choices.length === 0) return false;
  w.pendingEvent = { id: def.id, choices, wave: w.wave };
  w.lastEventWave = w.wave;
  pushLog(w.events, `${def.title}. ${def.summary}`, LogTone.Lore);
  pushEvent(w.events, EV.PhaseChange, w.map.hearthX, w.map.hearthY, 0, 0, Team.Player, 0, 0, def.id);
  return true;
}

/** `DebugEvent`: force a named event, or the first eligible one. */
export function forceChronicle(w: World, id: string): boolean {
  if (w.pendingEvent) return false;
  const def = id ? chronicleEvent(id) : eligibleEvents(w, w.eventHistory)[0];
  if (!def) return false;
  return openChronicle(w, def);
}

/**
 * Snapshot how the player actually defended, as a rolling mean. This is the
 * only input to the counter-picking above.
 */
export function sampleProfile(w: World): void {
  const u = w.units;
  let archers = 0;
  let melee = 0;
  let casters = 0;
  let total = 0;

  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) !== 0) continue;
    total++;
    if ((u.flags[i] & UF.Ranged) !== 0) archers++;
    else if ((u.flags[i] & UF.Caster) !== 0) casters++;
    else melee++;
  }

  let wallTiles = 0;
  let wallHealth = 0;
  let wallMax = 0;
  for (let k = 0; k < w.map.wall.length; k++) {
    if (w.map.wall[k] === WallKind.None) continue;
    wallTiles++;
    wallHealth += w.map.wallHp[k];
    wallMax += w.map.wallMaxHp[k];
  }

  // Economy exposure: producers far from the Hearth are inviting a raid.
  let exposed = 0;
  let producers = 0;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.team[i] !== Team.Player) continue;
    if (buildingType(b.typeId[i]).category !== 'economy') continue;
    producers++;
    const d = Math.hypot(b.tx[i] - w.map.hearthX, b.ty[i] - w.map.hearthY);
    if (d > 14) exposed++;
  }

  const p = w.profile;
  const n = Math.min(3, p.samples + 1);
  const blend = (old: number, next: number) => old + (next - old) / n;

  p.archerRatio = blend(p.archerRatio, total > 0 ? archers / total : 0);
  p.meleeMass = blend(p.meleeMass, total > 0 ? melee / total : 0);
  // Casting pressure is both the Sovereign's powers and the casters on the field.
  const casterRatio = total > 0 ? casters / total : 0;
  p.spellUse = blend(
    p.spellUse,
    clamp01((statsOf(w).powersCast / Math.max(1, w.wave)) / 6 + casterRatio),
  );
  p.wallIntegrity = blend(p.wallIntegrity, wallMax > 0 ? clamp01((wallHealth / wallMax) * clamp01(wallTiles / 90)) : 0);
  p.chokeReliance = blend(p.chokeReliance, chokeScore(w, wallTiles));
  p.economyExposure = blend(p.economyExposure, producers > 0 ? exposed / producers : 0.2);
  p.samples = Math.min(3, p.samples + 1);
}

/** A wall ring with very few openings reads as choke-point play. */
function chokeScore(w: World, wallTiles: number): number {
  if (wallTiles < 20) return 0;
  let gates = 0;
  for (let k = 0; k < w.map.wall.length; k++) if (w.map.wall[k] === WallKind.Gate) gates++;
  const openness = gates / Math.max(1, wallTiles / 20);
  return clamp01(1 - openness) * clamp01(wallTiles / 60);
}

/**
 * The climb.
 *
 * Runs every tick of the settlement, through sieges and build phases alike.
 * There is deliberately no pause and no way to abandon: the cost is already
 * spent, and being unable to stop is what makes the window mean something.
 */
export function stepAgeResearch(w: World, dt: number): void {
  if (w.researchingAge <= 0) return;
  w.ageResearch += dt;
  const need = ageResearchSeconds(w.researchingAge);
  if (w.ageResearch < need) return;

  w.age = w.researchingAge;
  w.researchingAge = 0;
  w.ageResearch = 0;
  pushLog(w.events, `A new Age turns: ${AGE_NAMES[w.age - 1] ?? 'the Hollow'}.`, LogTone.Lore);
  pushEvent(w.events, EV.Levelup, w.map.hearthX, w.map.hearthY, w.age, 0, Team.Player);
}

/** Can the player commit to the next Age right now, and if not, why not? */
export function ageBlockedReason(w: World): string {
  if (w.battle) return 'A field battle cannot research an Age.';
  if (w.researchingAge > 0) return 'Already under way.';
  const next = w.age + 1;
  if (next > MAX_AGE) return 'There is no Age after this one.';
  const floor = ageWaveFloor(next);
  // Campaign towns develop through industry; they have no scheduled waves.
  if (!w.config.homeCity && w.wave < floor) return `Not before wave ${floor}.`;
  const b = w.buildings;
  const hearthTier = w.hearth >= 0 && b.isAlive(w.hearth) ? b.tier[w.hearth] : 0;
  if (hearthTier < ageHearthTier(next)) return `Upgrade the Hearth to tier ${ageHearthTier(next)}.`;
  const standing = new Set<string>();
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] && b.team[i] === Team.Player && b.build[i] >= 1) {
      standing.add(buildingType(b.typeId[i]).id);
    }
  }
  const missing = (AGE_BUILDING_REQUIREMENTS[next] ?? []).filter((id) => !standing.has(id));
  if (missing.length) return `Complete ${missing.map((id) => BUILDING_TYPES.find((t) => t.id === id)?.name ?? id).join(', ')}.`;
  const bill = ageCost(next);
  if (!crownAfford(w, bill)) {
    const stock = w.purse ? crownPool(w.purse.members) : w.res;
    return 'Need ' + RESOURCE_KEYS.filter((k) => (bill[k] ?? 0) > stock[k])
      .map((k) => `${Math.ceil((bill[k] ?? 0) - stock[k])} ${RESOURCE_LABELS[k]}`).join(', ') + '.';
  }
  return '';
}

/**
 * Used by the harness and the debug console to jump straight to a wave.
 *
 * It deliberately does *not* touch the Age. It used to, back when the Age came
 * off the wave clock and "wave 40, still Ember" was a state the game could not
 * reach. Now it is exactly the state of a player who never paid for the climb —
 * and if this promoted you, the next Age would always sit below its own wave
 * floor and there would be no way to reach a researchable state from the
 * console at all. Jumping the wave is a debug convenience; being handed an Age
 * is not.
 */
export function forceWave(w: World, wave: number): void {
  w.wave = Math.max(0, wave - 1);
  planNextWave(w);
}

/** Total threat still standing, used by the UI's siege progress bar. */
export function remainingThreat(w: World): number {
  const u = w.units;
  let sum = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Invader) continue;
    sum += unitType(u.typeId[i]).threat * (u.hp[i] / Math.max(1, u.maxHp[i]));
  }
  for (const s of w.spawnQueue) sum += unitType(s.typeIdx).threat * s.count;
  return sum;
}

/** Distinct unit types the invader may field, for the codex. */
export function invaderCatalogue(factionId: string): number[] {
  return UNIT_TYPES.map((t, i) => (t.faction === factionId ? i : -1)).filter((i) => i >= 0);
}

export function randomGateIndex(w: World): number {
  return nextInt(w.rngDirector, 0, Math.max(0, w.map.gates.length - 1));
}

export function clampBudget(v: number): number {
  return clamp(v, 0, 1e7);
}
